import * as React from "react";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { Map } from "./components/Map";
import { Marker } from "./types/Marker";
import { SearchContext } from "./components/SearchContext";
import { SearchContext as SearchContextType } from "./types/SearchContext";
import { showIf } from "./helpers";
import { TypekitLoader } from "./components/TypekitLoader";
import { Categories } from "./types/Categories";
import { NewsSidebar } from "./components/NewsSidebar";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Media } from "./components/Media";
import { Media as MediaType } from "./types/Media";
import { Search } from "./components/Search";
import { Around } from "./types/Around";

export interface AppProps {
    hasMap: boolean;
    markers?: Marker[];
    around?: Around;
    couldNotResolveLocation: boolean;

    hasSearch: boolean;
    searchContext?: SearchContextType;

    hasNewsSidebar: boolean;
    newsCategories?: Categories;

    hasMedia: boolean;
    media?: MediaType[];

    cookieConsent: Record<string, boolean>;
}

export const App: React.FunctionComponent<AppProps> = ({
    hasMap,
    markers,
    around,

    hasSearch,
    searchContext,
    couldNotResolveLocation,

    hasNewsSidebar,
    newsCategories,

    hasMedia,
    media,

    cookieConsent
}) => {
    const mapContainer = useRef(document.getElementById("map"));
    const newsSidebarContainer = useRef(
        document.getElementById("news-sidebar")
    );
    const mediaContainer = useRef(document.getElementById("media-root"));

    const searchContainers = useRef([
        document.getElementById("search-normal"),
        document.getElementById("search-mobile"),
        document.getElementById("search-attached")
    ]);

    return (
        <>
            <TypekitLoader id="add2qak" />

            {showIf(hasMap && mapContainer.current, () =>
                createPortal(
                    <ErrorBoundary>
                        <Map markers={markers} around={around} />
                    </ErrorBoundary>,
                    mapContainer.current
                )
            )}

            {hasSearch &&
                searchContainers.current.map(c =>
                    showIf(c, () =>
                        createPortal(
                            <ErrorBoundary>
                                <SearchContext.Provider value={searchContext}>
                                    <Search
                                        container={c}
                                        type={c.id.replace(/search-/, "")}
                                        couldNotResolveLocation={
                                            couldNotResolveLocation
                                        }
                                    />
                                </SearchContext.Provider>
                            </ErrorBoundary>,
                            c
                        )
                    )
                )}

            {showIf(hasNewsSidebar && newsSidebarContainer.current, () =>
                createPortal(
                    <ErrorBoundary>
                        <NewsSidebar categories={newsCategories} />
                    </ErrorBoundary>,
                    newsSidebarContainer.current
                )
            )}

            {showIf(hasMedia && mediaContainer.current, () =>
                createPortal(
                    <ErrorBoundary>
                        <Media media={media} cookieConsent={cookieConsent} />
                    </ErrorBoundary>,
                    mediaContainer.current
                )
            )}
        </>
    );
};

App.displayName = "App";
